import DownloadIcon from '@mui/icons-material/Download';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { Alert, Box, Button, Card, Typography } from '@mui/material';
import getSymbolFromCurrency from 'currency-symbol-map';
import React, { useEffect, useState } from 'react';
import ArrowReturnIcon from '../../../../../svg-icons/ArrowReturnIcon';
import RetractionFeesIcon from '../../../../../svg-icons/RetractionFeesIcon';
import FeesCalculations from './FeesCalculations.jsx';
import OrderPackagesList from './OrderPackagesList.jsx';
import RetractionFeesRecapModal from './RetractionFeesRecapModal.jsx';

const RetractionFeesForm = ({
  orderDetails,
  handleShowComponent,
  isFromDrawwer,
  isChildComponent,
  getRetractionFees,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPackages, setSelectedPackages] = useState([]);
  const [retractionType, setRetractionType] = useState('total');
  const [retractFeeAmount, setRetractFeeAmount] = useState(0);
  const [retractFeeAmountEuros, setRetractFeeAmountEuros] = useState(0);
  const [refundAmount, setRefundAmount] = useState(0);
  const [partialRefundAmount, setPartialRefundAmount] = useState(0);
  const [calculMethod, setCalculMethod] = useState('total');
  const [carrierOption, setCarrierOption] = useState(null);
  const [disabled, setDisabled] = useState(false);
  const [productsAmount, setProductsAmount] = useState(0);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [forcePackageFees, setForcePackageFees] = useState(false);

  const onClose = () => {
    setIsOpen(false);
  };

  const handleDownloadRetractionFile = () => {
    const filePath = '/assets/HR-CALCULATEUR-RETRACTATION-V2.xlsx';

    const link = document.createElement('a');
    link.href = filePath;
    link.download = 'HR-CALCULATEUR-RETRACTATION-V2.xlsx';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    if (!orderDetails?.contact?.store?.hasRetractFees && !forcePackageFees) {
      setCalculMethod('withoutFees');
      setRetractFeeAmount(0);
      setRefundAmount(orderDetails?.amounts?.ttc?.toFixed(2));
      setPartialRefundAmount(0);
    }
  }, [orderDetails]);

  useEffect(() => {
    if (!selectedPackages.length || !orderDetails) {
      return;
    }

    const packageFeesTotal = selectedPackages.reduce((acc, pkg) => {
      if (orderDetails?.currency?.code === 'EUR') {
        return acc + pkg.packageFeesEuros;
      } else {
        return acc + pkg.packageFeesConverted;
      }
    }, 0);
    const packageFeesTotalEuros = selectedPackages.reduce((acc, pkg) => {
      return acc + pkg.packageFeesEuros;
    }, 0);
    // pas de frais de rétractations
    if (calculMethod === 'withoutFees') {
      setRetractFeeAmount(0);
      setRefundAmount(orderDetails?.amounts?.ttc?.toFixed(2));
      setPartialRefundAmount(0);
      // frais de rétractations appliqués par zone
    } else if (calculMethod === 'total') {
      setRetractFeeAmount(packageFeesTotal.toFixed(2));
      setRetractFeeAmountEuros(packageFeesTotalEuros.toFixed(2));
      setRefundAmount((productsAmount - packageFeesTotal).toFixed(2));
      setPartialRefundAmount((packageFeesTotal / 2).toFixed(2));
      // montant libre à définir
    } else if (calculMethod === 'free') {
      setPartialRefundAmount((retractFeeAmount / 2).toFixed(2));
    }

    if (retractFeeAmount && retractFeeAmount < 200) {
      setCarrierOption('DPD ou Colissimo');
    } else {
      setCarrierOption('DPD');
    }
    setTimeout(() => {
      getRetractionFees({
        selectedPackages,
        retractionType,
        retractFeeAmount,
        refundAmount,
        partialRefundAmount,
        calculMethod,
        carrierOption,
        orderAmount: orderDetails?.amounts?.ttc,
        currencyCode: orderDetails?.currency?.code,
        productsAmount,
      });
    }, 200);
  }, [
    orderDetails,
    selectedPackages,
    retractionType,
    calculMethod,
    retractFeeAmount,
    refundAmount,
    partialRefundAmount,
    carrierOption,
    productsAmount,
    retractFeeAmountEuros,
  ]);

  useEffect(() => {
    const totalProductAmount = selectedProducts.reduce((acc, product) => {
      return acc + product.productAmount;
    }, 0);

    setProductsAmount(totalProductAmount.toFixed(2));
  }, [selectedProducts]);

  return (
    <Box>
      {!isChildComponent && (
        <Card
          elevation={5}
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: '100%',
            padding: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <RetractionFeesIcon fontSize="small" />
              <Typography color="primary">
                Simulation des frais de rétractation
              </Typography>
            </Box>
            <Button
              onClick={() => handleShowComponent(null)}
              variant="outlined"
              size="small"
              sx={{ position: 'absolute', top: 16, left: 16 }}
            >
              <ArrowReturnIcon fontSize="small" />
              <span style={{ marginLeft: 4, fontSize: '0.9em' }}>Retour</span>
            </Button>
          </Box>
        </Card>
      )}
      <Box
        sx={
          isChildComponent
            ? {}
            : {
                overflowY: 'scroll',
                maxHeight: isFromDrawwer ? '90vh' : '65vh',
              }
        }
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', gap: 1, mt: 2, mb: 2 }}>
            <Button
              onClick={() => setRetractionType('total')}
              variant={retractionType === 'total' ? 'contained' : 'outlined'}
              size="small"
              sx={{ fontSize: '0.7em', padding: '0 0.5em' }}
            >
              Totale
            </Button>
            <Button
              disabled={disabled}
              onClick={() => setRetractionType('partial')}
              variant={retractionType === 'partial' ? 'contained' : 'outlined'}
              size="small"
              sx={{ fontSize: '0.7em', padding: '0 0.5em' }}
            >
              Partielle
            </Button>
          </Box>
          <Button
            variant="link"
            size="small"
            sx={{ fontSize: '0.7em', padding: '0 0.5em' }}
            endIcon={<DownloadIcon />}
            onClick={handleDownloadRetractionFile}
          >
            Fichier de calcul des frais
          </Button>
        </Box>
        {orderDetails?.amounts?.discount?.code && (
          <Alert
            variant="outlined"
            color="warning"
            sx={{ m: 2 }}
            icon={<LocalOfferIcon />}
          >
            La commande à une réduction appliquée:{' '}
            {orderDetails?.amounts?.discount?.code} -{' '}
            {orderDetails?.amounts?.discount?.value}
            {' | '}
            {`${orderDetails?.amounts?.discount?.amount}${getSymbolFromCurrency(orderDetails?.currency?.code)}`}
          </Alert>
        )}
        <Typography
          variant="h6"
          color="primary"
          sx={{ fontSize: '1em', fontWeight: 'bold' }}
        >
          Commande concernée
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <div
            style={{
              width: '50%',
              border: '1px solid black',
              padding: '0.5em 1em',
              borderRadius: '10px',
            }}
          >
            <Typography
              sx={{ fontSize: '0.8em', color: 'primary.main', mb: 1 }}
            >
              Référence
            </Typography>
            <Typography sx={{ fontSize: '0.9em' }}>
              {orderDetails?.reference}
            </Typography>
          </div>
          <div
            style={{
              width: '50%',
              border: '1px solid black',
              padding: '0.5em 1em',
              borderRadius: '10px',
            }}
          >
            <Typography
              sx={{ fontSize: '0.8em', color: 'primary.main', mb: 1 }}
            >
              Montant de la commande
            </Typography>
            <Typography sx={{ fontSize: '0.9em' }}>{`${
              orderDetails?.amounts?.ttcNet
            } ${getSymbolFromCurrency(
              orderDetails?.currency?.code
            )}`}</Typography>
          </div>
        </Box>

        <OrderPackagesList
          orderDetails={orderDetails}
          selectedPackages={selectedPackages}
          setSelectedPackages={setSelectedPackages}
          retractionType={retractionType}
          setRetractionType={setRetractionType}
          disabled={disabled}
          setDisabled={setDisabled}
          productsAmount={productsAmount}
          setProductsAmount={setProductsAmount}
          selectedProducts={selectedProducts}
          setSelectedProducts={setSelectedProducts}
        />

        <FeesCalculations
          countryZone={orderDetails?.countryZone}
          calculMethod={calculMethod}
          setCalculMethod={setCalculMethod}
          setRefundAmount={setRefundAmount}
          retractFeeAmount={retractFeeAmount}
          setRetractFeeAmount={setRetractFeeAmount}
          retractFeeAmountEuros={retractFeeAmountEuros}
          refundAmount={refundAmount}
          partialRefundAmount={partialRefundAmount}
          currencyCode={orderDetails?.currency?.code}
          carrierOption={carrierOption}
          productsAmount={productsAmount}
          store={orderDetails?.contact?.store}
          setForcePackageFees={setForcePackageFees}
          forcePackageFees={forcePackageFees}
        />
        {!isChildComponent && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={() => setIsOpen(true)}
              sx={{ mt: 2 }}
            >
              Voir le résumé
            </Button>
          </Box>
        )}
        <RetractionFeesRecapModal
          isFromDrawwer={isFromDrawwer}
          retractionType={retractionType}
          calculMethod={calculMethod}
          retractFeeAmount={retractFeeAmount}
          refundAmount={refundAmount}
          partialRefundAmount={partialRefundAmount}
          currencyCode={orderDetails?.currency?.code}
          orderReference={orderDetails?.reference}
          orderAmount={orderDetails?.amounts?.ttcNet}
          productsAmount={productsAmount}
          packageCount={selectedPackages.length}
          ticketReference={orderDetails?.ticket?.reference}
          carrierOption={carrierOption}
          isOpen={isOpen}
          onClose={onClose}
        />
      </Box>
    </Box>
  );
};

export default RetractionFeesForm;
